import React, { useState, useEffect, useRef } from "react";
import { IonModal, IonIcon, IonButton } from "@ionic/react";
import { close } from "ionicons/icons";

/**
 * Data interface for activity recording
 */
interface ActivityData {
  date: Date;
  value: number;
}

/**
 * Date information structure used in component
 */
interface DateInfo {
  date: Date;
  dayName: string;
  dayNumber: number;
  isSelected: boolean;
  isDisabled: boolean;
  month: number;
  year: number;
}

/**
 * Props interface for RecordActivity component
 */
interface RecordActivityProps {
  // Required props
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: ActivityData) => void;

  // Customization props
  title?: string;
  subtitle?: string;
  activityLabel?: string;
  saveButtonText?: string;

  // Activity options
  minValue?: number;
  maxValue?: number;
  initialValue?: number;

  // Date selection
  initialDate?: Date;

  // Style customization
  accentColor?: string;
  lightAccentColor?: string;
  buttonSize?: number;
  buttonGap?: number;

  // Month range
  monthsBack?: number;
}

/**
 * RecordActivity Component
 *
 * A modal component that allows users to record an activity by selecting
 * a date and value. Features a horizontally scrollable date picker with
 * visual indicators for current and disabled dates.
 */
const RecordActivity: React.FC<RecordActivityProps> = ({
  // Required props
  isOpen = false,
  onClose,
  onSave,

  // Customization with defaults
  title = "Record Activity",
  subtitle = "",
  activityLabel = "completed activities",
  saveButtonText = "Save",

  // Activity options with defaults
  minValue = 0,
  maxValue = 5,
  initialValue = 0,

  // Date selection with default
  initialDate = new Date(),

  // Style customization with defaults
  accentColor = "#00A9CE",
  lightAccentColor = "",
  buttonSize = 56,
  buttonGap = 8,

  // Month range - default to 24 months (2 years) back
  monthsBack = 24,
}) => {
  // State variables
  const [selectedDate, setSelectedDate] = useState(new Date(initialDate));
  const [selectedValue, setSelectedValue] = useState(initialValue);
  const [visibleDates, setVisibleDates] = useState<DateInfo[]>([]);
  const [currentlyViewingMonth, setCurrentlyViewingMonth] =
    useState<string>("");
  const [modalReady, setModalReady] = useState<boolean>(false);

  // Refs for DOM manipulation and tracking state between renders
  const daysContainerRef = useRef<HTMLDivElement>(null);
  const valuesContainerRef = useRef<HTMLDivElement>(null);
  const scrollingRef = useRef<boolean>(false);
  const scrollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const selectedDateRef = useRef<Date | null>(null);
  const selectedElementRef = useRef<HTMLElement | null>(null);
  const focusAttemptsRef = useRef<number>(0);
  const maxFocusAttempts = 10;

  // Date boundaries calculation
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const futureDateLimit = new Date(today);
  futureDateLimit.setDate(futureDateLimit.getDate() + 3);
  futureDateLimit.setHours(0, 0, 0, 0);

  const oldestAllowed = new Date(today);
  oldestAllowed.setMonth(today.getMonth() - monthsBack);
  oldestAllowed.setHours(0, 0, 0, 0);

  /**
   * Generates a continuous array of dates for the scrollable calendar
   * starting from old dates and extending to the future date limit
   */
  const generateScrollableDates = (centerDate: Date) => {
    const dates: DateInfo[] = [];

    // Calculate start date (2 days before center date)
    const startDate = new Date(centerDate);
    startDate.setDate(startDate.getDate() - 2);

    if (startDate < oldestAllowed) {
      startDate.setTime(oldestAllowed.getTime());
    }

    const endDate = new Date(futureDateLimit);

    // Generate all dates in the range
    const current = new Date(startDate);
    while (current <= endDate) {
      dates.push({
        date: new Date(current),
        dayName: current.toLocaleDateString("en-US", { weekday: "short" }),
        dayNumber: current.getDate(),
        isSelected: current.toDateString() === selectedDate.toDateString(),
        isDisabled: current > today,
        month: current.getMonth(),
        year: current.getFullYear(),
      });

      current.setDate(current.getDate() + 1);
    }

    return dates;
  };

  // Reset focus attempts when modal opens or closes
  useEffect(() => {
    focusAttemptsRef.current = 0;

    if (isOpen) {
      setModalReady(false);
      setTimeout(() => setModalReady(true), 100);
    }
  }, [isOpen]);

  // Initialize modal with today's date when opened
  useEffect(() => {
    if (isOpen) {
      const currentDay = new Date(today);

      selectedDateRef.current = currentDay;
      setSelectedDate(currentDay);
      setSelectedValue(initialValue);
      const dates = generateScrollableDates(currentDay);
      setVisibleDates(dates);
      setCurrentlyViewingMonth(formatMonthYear(currentDay));
    }
  }, [isOpen, initialValue, monthsBack]);

  // Regenerate dates when selected date changes
  useEffect(() => {
    selectedDateRef.current = selectedDate;
    setVisibleDates(generateScrollableDates(selectedDate));
  }, [selectedDate]);

  // Center the selected date in view when date list changes
  useEffect(() => {
    centerSelectedDate();
  }, [visibleDates]);

  /**
   * Centers the selected date element in the scrollable container
   */
  const centerSelectedDate = () => {
    if (daysContainerRef.current && !scrollingRef.current) {
      const selectedElement =
        daysContainerRef.current.querySelector(".selected-date");
      if (selectedElement) {
        selectedElementRef.current = selectedElement as HTMLElement;

        const container = daysContainerRef.current;
        const scrollLeft =
          selectedElement.getBoundingClientRect().left +
          container.scrollLeft -
          container.getBoundingClientRect().left -
          container.clientWidth / 2 +
          selectedElement.getBoundingClientRect().width / 2;

        scrollingRef.current = true;
        container.scrollTo({ left: scrollLeft, behavior: "smooth" });

        if (scrollingTimeoutRef.current) {
          clearTimeout(scrollingTimeoutRef.current);
        }

        scrollingTimeoutRef.current = setTimeout(() => {
          scrollingRef.current = false;
        }, 500);
      }
    }
  };

  // Repeatedly attempt to focus the selected date when modal is ready
  useEffect(() => {
    if (isOpen && modalReady && focusAttemptsRef.current < maxFocusAttempts) {
      const focusTimer = setInterval(() => {
        if (focusAttemptsRef.current >= maxFocusAttempts) {
          clearInterval(focusTimer);
          return;
        }

        focusAttemptsRef.current++;
        const focused = attemptToFocusSelectedDate();

        if (focused) {
          clearInterval(focusTimer);
        }
      }, 100);

      return () => clearInterval(focusTimer);
    }
  }, [isOpen, modalReady, visibleDates]);

  // Set proper tabIndex values for accessible keyboard navigation
  useEffect(() => {
    if (daysContainerRef.current) {
      const dateElements =
        daysContainerRef.current.querySelectorAll("[data-date]");
      dateElements.forEach((element) => {
        const isSelected = element.classList.contains("selected-date");
        (element as HTMLElement).tabIndex = isSelected ? 0 : -1;

        if (isSelected) {
          selectedElementRef.current = element as HTMLElement;
        }
      });
    }
  }, [visibleDates]);

  // Focus and center the selected date after IonModal animation completes
  useEffect(() => {
    if (isOpen && modalReady) {
      const timers: NodeJS.Timeout[] = [];

      [50, 150, 250, 350, 500, 750, 1000].forEach((delay) => {
        const timer = setTimeout(() => {
          attemptToFocusSelectedDate();
          centerSelectedDate();
        }, delay);

        timers.push(timer);
      });

      return () => {
        timers.forEach((timer) => clearTimeout(timer));
      };
    }
  }, [isOpen, modalReady]);

  /**
   * Attempts to focus the currently selected date element
   * Returns true if successful, false otherwise
   */
  const attemptToFocusSelectedDate = (): boolean => {
    // Try the cached element reference first
    if (selectedElementRef.current) {
      try {
        selectedElementRef.current.focus({ preventScroll: true });
        return document.activeElement === selectedElementRef.current;
      } catch (e) {
        console.error("Failed to focus cached element:", e);
      }
    }

    // Fall back to query selector if cached element didn't work
    if (daysContainerRef.current) {
      const selectedElement =
        daysContainerRef.current.querySelector(".selected-date");
      if (selectedElement) {
        try {
          (selectedElement as HTMLElement).focus({ preventScroll: true });
          selectedElementRef.current = selectedElement as HTMLElement;
          return document.activeElement === selectedElement;
        } catch (e) {
          console.error("Failed to focus element:", e);
        }
      }
    }

    return false;
  };

  /**
   * Updates the current month display based on the date element
   * closest to the center of the viewport
   */
  const updateCurrentViewingMonth = () => {
    if (!daysContainerRef.current) return;

    const container = daysContainerRef.current;
    const containerRect = container.getBoundingClientRect();
    const containerCenter = containerRect.left + containerRect.width / 2;

    const dateElements = container.querySelectorAll("[data-date]");
    if (dateElements.length === 0) return;

    // Find the element closest to the center
    let closestElement = dateElements[0];
    let closestDistance = Math.abs(
      closestElement.getBoundingClientRect().left - containerCenter
    );

    dateElements.forEach((element) => {
      const distance = Math.abs(
        element.getBoundingClientRect().left - containerCenter
      );
      if (distance < closestDistance) {
        closestDistance = distance;
        closestElement = element;
      }
    });

    // Update month display based on the centered date
    const dateString = closestElement.getAttribute("data-date");
    if (dateString) {
      const date = new Date(dateString);
      setCurrentlyViewingMonth(formatMonthYear(date));
    }
  };

  /**
   * Handles date selection from the calendar
   * Will only allow selecting dates within the valid range
   */
  const handleDateSelect = (date: Date) => {
    if (date <= futureDateLimit && date >= oldestAllowed) {
      if (date <= today) {
        selectedDateRef.current = date;
        setSelectedDate(new Date(date));

        // Reset focus attempts when date changes
        focusAttemptsRef.current = 0;

        setTimeout(() => {
          centerSelectedDate();
          attemptToFocusSelectedDate();
        }, 50);
      }
    }
  };

  /**
   * Handles scrolling events in the date container
   * Updates the visible month and loads more dates when needed
   */
  const handleScroll = () => {
    if (!daysContainerRef.current) return;

    updateCurrentViewingMonth();

    if (scrollingRef.current) return;

    const container = daysContainerRef.current;
    const scrollPosition = container.scrollLeft;

    // Load more dates when scrolling near left edge (earlier dates)
    if (scrollPosition < 100) {
      // Near left edge - load earlier dates
      const earliestDate = new Date(visibleDates[0].date);
      earliestDate.setDate(earliestDate.getDate() - 14); // Load 2 more weeks

      if (earliestDate >= oldestAllowed) {
        scrollingRef.current = true;

        // Calculate new dates to add
        const newDates = generateScrollableDates(earliestDate);
        const filteredNewDates = newDates.filter(
          (newDate) =>
            !visibleDates.some(
              (existingDate) =>
                existingDate.date.toDateString() === newDate.date.toDateString()
            )
        );

        // Keep track of the current scroll position
        const currentScroll = container.scrollLeft;
        const scrollContent = container.querySelector("div");
        const contentWidth = scrollContent ? scrollContent.offsetWidth : 0;

        // Update the dates
        setVisibleDates((prevDates) => {
          const mergedDates = [...filteredNewDates, ...prevDates];
          // Deduplicate dates
          const uniqueDates: DateInfo[] = [];
          const dateStrings = new Set();

          for (const date of mergedDates) {
            const dateString = date.date.toDateString();
            if (!dateStrings.has(dateString)) {
              dateStrings.add(dateString);
              uniqueDates.push({
                ...date,
                isSelected: selectedDateRef.current
                  ? date.date.toDateString() ===
                    selectedDateRef.current.toDateString()
                  : date.isSelected,
              });
            }
          }

          return uniqueDates.sort(
            (a, b) => a.date.getTime() - b.date.getTime()
          );
        });

        // After render, adjust the scroll position to maintain the view
        setTimeout(() => {
          if (container) {
            const newScrollContent = container.querySelector("div");
            const newContentWidth = newScrollContent
              ? newScrollContent.offsetWidth
              : 0;
            const widthDifference = newContentWidth - contentWidth;
            container.scrollLeft = currentScroll + widthDifference;

            setTimeout(() => {
              scrollingRef.current = false;

              // Re-center the selected date if we've loaded new dates
              if (filteredNewDates.length > 0) {
                centerSelectedDate();
              }
            }, 100);
          }
        }, 10);
      }
    }
  };

  /**
   * Formats a date as "Month Year" (e.g., "January 2025")
   */
  const formatMonthYear = (date: Date) => {
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  };

  /**
   * Generates the numeric options for activity values
   */
  const generateOptions = () => {
    const options = [];
    for (let i = minValue; i <= maxValue; i++) {
      options.push(i);
    }
    return options;
  };

  /**
   * Handles the save button click
   * Calls the onSave callback with selected date and value
   */
  const handleSave = () => {
    onSave({
      date: selectedDate,
      value: selectedValue,
    });
    onClose();
  };

  /**
   * Handles keyboard navigation for date selection
   */
  const handleKeyDown = (e: React.KeyboardEvent, day: DateInfo) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (!day.isDisabled) {
        handleDateSelect(day.date);
      }
    }
  };

  /**
   * Called after the modal is fully presented
   */
  const handleAfterPresent = () => {
    setModalReady(true);
    focusAttemptsRef.current = 0;
  };

  // Generate activity value options
  const activityOptions = generateOptions();

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      onDidPresent={handleAfterPresent}
      className="container-modal"
      style={{
        "--border-radius": "30px",
        "--width": "95%",
        "--height": "auto",
      }}
    >
      <div
        className="flex flex-col items-center px-1 p-2 pb-8 lg:m-2"
        style={{
          height: "fit-content",
          maxHeight: "90svh",
          overflowY: "auto",
        }}
      >
        {/* Modal header */}
        <div className="w-full flex justify-center items-center relative mb-6">
          <h2
            className="text-2xl font-bold "
            style={{ color: "#504f4e", paddingTop: "12px" }}
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 absolute top-0 p-2 right-0"
            aria-label="Close"
          >
            <IonIcon icon={close} style={{ fontSize: "30px" }} />
          </button>
        </div>

        {/* Month/year display */}
        <div className="text-xl font-medium mb-6">{currentlyViewingMonth}</div>

        {/* Horizontal date picker */}
        <div
          ref={daysContainerRef}
          className="w-full mb-8 overflow-x-auto pb-4 border-b scrollbar-hide"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            minHeight: "90px",
          }}
          onScroll={handleScroll}
          aria-label="Date selection calendar"
        >
          <div className="flex min-w-max px-4" style={{ paddingBottom: "8px" }}>
            {visibleDates.map((day, index) => {
              // Highlight first day of each month
              const isFirstOfMonth =
                day.dayNumber === 1 ||
                (index > 0 && day.month !== visibleDates[index - 1].month);

              return (
                <div
                  key={index}
                  className={`flex flex-col items-center px-4 py-2 mx-1 rounded-lg transition-all duration-200 ${
                    day.isSelected ? "selected-date bg-blue-100" : ""
                  } ${isFirstOfMonth ? "ml-6" : ""} ${
                    day.isDisabled
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  }`}
                  onClick={() => !day.isDisabled && handleDateSelect(day.date)}
                  onKeyDown={(e) => handleKeyDown(e, day)}
                  data-date={day.date.toISOString()}
                  data-month={day.month}
                  data-year={day.year}
                  tabIndex={day.isSelected ? 0 : -1}
                  role="button"
                  aria-pressed={day.isSelected}
                  aria-disabled={day.isDisabled}
                  aria-label={`${day.date.toLocaleDateString("en-US", {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                  })}`}
                >
                  <div
                    className={`text-base ${
                      day.isSelected
                        ? "text-blue-500 font-semibold"
                        : "text-gray-700"
                    }`}
                  >
                    {day.dayName}
                  </div>
                  <div
                    className={`text-2xl font-bold ${
                      day.isSelected ? "text-blue-500" : "text-gray-700"
                    }`}
                  >
                    {day.dayNumber}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Optional subtitle */}
        {subtitle && <div className="mb-4 text-lg">{subtitle}</div>}

        {/* Activity value selector */}
        <div
          ref={valuesContainerRef}
          className="w-full mb-8 overflow-x-auto pb-2 scrollbar-hide"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            minHeight: `${buttonSize + 16}px`,
          }}
          aria-label="Activity value selection"
        >
          <div
            className="flex justify-center min-w-max px-4"
            style={{ gap: `${buttonGap}px` }}
          >
            {activityOptions.map((value) => (
              <button
                key={value}
                onClick={() => setSelectedValue(value)}
                className="rounded-full flex items-center justify-center text-2xl font-bold transition-all duration-200"
                style={{
                  width: `${buttonSize}px`,
                  height: `${buttonSize}px`,
                  backgroundColor:
                    selectedValue === value ? accentColor : "transparent",
                  borderWidth: "2px",
                  borderStyle: "solid",
                  borderColor: accentColor,
                  color: selectedValue === value ? "white" : accentColor,
                }}
                aria-label={`Select ${value} ${activityLabel}`}
                aria-pressed={selectedValue === value}
              >
                {value}
              </button>
            ))}
          </div>
        </div>

        {/* Activity label indicator */}
        <div
          className="rounded-full py-2 px-8 mb-8"
          // style={{ backgroundColor: lightAccentColor }}
        >
          <span className="text-base">{activityLabel}</span>
        </div>

        {/* Save button */}
        <div className="w-full flex flex-col items-center gap-5 mb-2">
          <IonButton
            expand="block"
            onClick={handleSave}
            className="primary-button"
            style={{
              "--border-radius": "24px",
              "--height": "46px",
              marginBottom: "12px",
              padding: "12px 20px",
              fontSize: "22px",
            }}
          >
            {saveButtonText}
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
};

export default RecordActivity;
